import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Paper,
  Box,
  Typography,
  Stack,
  TextField,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Rectangle,
} from "recharts";
import { API_URL } from "../../lib/constants";
import { getValidAccessToken } from "../../lib/api/Auth";
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import { ErrorLogItem } from "./ErrorsTab";

export interface SystemErrorsChartProps {
  environment: string;
  source: string;
  severity: string;
  resolvedFilter: string;
}

export const formatLocalDateKey = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseLocalDateKey = (str: string): Date => {
  const [year, month, day] = str.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const getLastWeekDateString = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 6); // 7 days inclusive of today
  return formatLocalDateKey(d);
};

export const getTodayDateString = (): string => {
  return formatLocalDateKey(new Date());
};

// Known severity colors with warning on top
export const SEVERITY_COLORS: Record<string, string> = {
  fatal: "#9c27b0", // Purple
  error: "#d32f2f", // Red
  info: "#0288d1",  // Blue
  warn: "#ed6c02",  // Orange / Warning on top
  warning: "#ed6c02",
};

export const KNOWN_SEVERITIES = ["fatal", "error", "info", "warn"];

export const SEVERITY_LABELS: Record<string, string> = {
  fatal: "Fatal",
  error: "Error",
  warn: "Warning",
  info: "Info",
};

export const SOURCE_DISPLAY_NAMES: Record<string, string> = {
  frontend: "Frontend (Main)",
  "frontend-prince": "Frontend (Prince)",
  "frontend-lincoln": "Frontend (Lincoln)",
  backend: "Backend",
};

export const SOURCE_INITIALS: Record<string, string> = {
  backend: "B",
  frontend: "F",
  "frontend-prince": "FP",
  "frontend-lincoln": "FL",
};

export const getSourceInitial = (src: string): string => {
  if (SOURCE_INITIALS[src]) return SOURCE_INITIALS[src];
  const parts = src.split("-");
  if (parts.length > 1) {
    return parts.map((p) => p[0]?.toUpperCase() || "").join("");
  }
  return src.slice(0, 2).toUpperCase();
};

export interface ChartAggregationResult {
  chartData: Array<Record<string, any>>;
  detectedSources: string[];
  detectedSeverities: string[];
}

export function aggregateErrorTrends(
  items: ErrorLogItem[],
  startDateStr: string,
  endDateStr: string,
  selectedSource: string = "all"
): ChartAggregationResult {
  if (!startDateStr || !endDateStr) {
    return { chartData: [], detectedSources: [], detectedSeverities: [] };
  }

  const start = parseLocalDateKey(startDateStr);
  const end = parseLocalDateKey(endDateStr);

  const daysMap: Record<string, Record<string, Record<string, number>>> = {};
  const sourcesSet = new Set<string>();
  const severitiesSet = new Set<string>();

  // Pre-fill days in local date range
  const curr = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (curr <= end) {
    const dateKey = formatLocalDateKey(curr);
    daysMap[dateKey] = {};
    curr.setDate(curr.getDate() + 1);
  }

  // Default target sources if filtered or present in data
  if (selectedSource !== "all") {
    sourcesSet.add(selectedSource);
  } else {
    sourcesSet.add("backend");
    sourcesSet.add("frontend");
    sourcesSet.add("frontend-prince");
    sourcesSet.add("frontend-lincoln");
  }

  items.forEach((item) => {
    if (!item.timestamp) return;
    const itemDateObj = new Date(item.timestamp);
    if (isNaN(itemDateObj.getTime())) return;

    const itemDate = formatLocalDateKey(itemDateObj);
    if (!daysMap[itemDate]) return;

    const itemSource = item.source || "unknown";
    sourcesSet.add(itemSource);

    let itemSev = (item.severity || "error").toLowerCase();
    if (itemSev === "warning") itemSev = "warn";
    severitiesSet.add(itemSev);

    if (!daysMap[itemDate][itemSource]) {
      daysMap[itemDate][itemSource] = {};
    }
    daysMap[itemDate][itemSource][itemSev] =
      (daysMap[itemDate][itemSource][itemSev] || 0) + 1;
  });

  const activeSources = Array.from(sourcesSet);
  const activeSeverities = Array.from(
    new Set([...KNOWN_SEVERITIES, ...Array.from(severitiesSet)])
  );

  const formattedData = Object.keys(daysMap)
    .sort()
    .map((dateKey) => {
      const row: Record<string, any> = { date: dateKey };
      activeSources.forEach((src) => {
        let srcTotal = 0;
        let topKey = "";

        // Iterate in stacking order: fatal -> error -> info -> warn (warn on top)
        const orderedSeverities = ["fatal", "error", "info", "warn"].filter((s) =>
          activeSeverities.includes(s)
        );

        orderedSeverities.forEach((sev) => {
          const key = `${src}__${sev}`;
          const count = daysMap[dateKey][src]?.[sev] || 0;
          row[key] = count;
          if (count > 0) {
            srcTotal += count;
            topKey = key; // The last non-zero severity in stacking order is the top of the stack
          }
        });

        row[`${src}__total`] = srcTotal;
        row[`${src}__topKey`] = topKey;
      });
      return row;
    });

  return {
    chartData: formattedData,
    detectedSources: activeSources,
    detectedSeverities: activeSeverities,
  };
}

export const renderBarShape = (src: string, sev: string) => (props: any) => {
  const { x, y, width, height, payload } = props;
  if (!width || !height || height <= 0) return null;

  const currentKey = `${src}__${sev}`;
  // Only render on the topmost non-zero bar in this stack
  const isTop = payload && payload[`${src}__topKey`] === currentKey;
  const initial = getSourceInitial(src);
  const centerX = Number(x || 0) + Number(width || 0) / 2;
  const topY = Number(y || 0) - 5;

  return (
    <g className="stack-bar-group">
      <Rectangle {...props} />
      {isTop && (
        <text
          x={centerX}
          y={topY}
          fill="#374151"
          textAnchor="middle"
          fontSize={11}
          fontWeight="bold"
        >
          {initial}
        </text>
      )}
    </g>
  );
};

export const renderStackLabel = renderBarShape;

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomChartTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const activeItems = payload.filter((p) => typeof p.value === "number" && p.value > 0);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 1.5,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        minWidth: 180,
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
        Date: {label}
      </Typography>
      {activeItems.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No errors or warnings recorded.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {activeItems.map((item) => {
            const dataKey = String(item.dataKey || "");
            const [src, sev] = dataKey.split("__");
            const srcLabel = SOURCE_DISPLAY_NAMES[src] || src;
            const sevLabel = SEVERITY_LABELS[sev] || sev.toUpperCase();
            const initial = getSourceInitial(src);
            const color = item.fill || item.color || "#757575";

            return (
              <Box
                key={dataKey}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: color,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    [{initial}] {srcLabel} ({sevLabel}):
                  </Typography>
                </Box>
                <Typography variant="caption" fontWeight="bold">
                  {item.value}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

export default function SystemErrorsChart({
  environment,
  source,
  severity,
  resolvedFilter,
}: SystemErrorsChartProps) {
  let userInfo: any;
  try {
    const userContext = useUserInfoContext();
    userInfo = userContext?.userInfo;
  } catch {
    // Graceful fallback if rendered outside UserInfoContextProvider
  }
  // TODO: Consider unifying date filters or adding a "Sync with table dates" toggle / preset dropdown (e.g. Last 7 Days, Last 30 Days) with ErrorsTab.
  const activeChartRequestIdRef = React.useRef<number>(0);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const [chartStartDate, setChartStartDate] = useState<string>(getLastWeekDateString());
  const [chartEndDate, setChartEndDate] = useState<string>(getTodayDateString());
  const [chartItems, setChartItems] = useState<ErrorLogItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchChartErrors = useCallback(async () => {
    const requestId = ++activeChartRequestIdRef.current;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setErrorMsg(null);
    try {
      const token = await getValidAccessToken(userInfo?.accessToken);
      if (!token) {
        if (requestId === activeChartRequestIdRef.current) {
          setErrorMsg("Authentication token unavailable. Please log in or refresh.");
          setLoading(false);
        }
        return;
      }
      const params = new URLSearchParams();
      if (environment !== "all") params.append("environment", environment);
      if (source !== "all") params.append("source", source);
      if (severity !== "all") params.append("severity", severity);
      if (resolvedFilter !== "all") params.append("resolved", resolvedFilter);

      if (chartStartDate) {
        const [sYear, sMonth, sDay] = chartStartDate.split("-").map(Number);
        const startD = new Date(sYear, sMonth - 1, sDay, 0, 0, 0, 0);
        if (!isNaN(startD.getTime())) {
          params.append("startDate", startD.toISOString());
        }
      }
      if (chartEndDate) {
        const [eYear, eMonth, eDay] = chartEndDate.split("-").map(Number);
        const endD = new Date(eYear, eMonth - 1, eDay, 23, 59, 59, 999);
        if (!isNaN(endD.getTime())) {
          params.append("endDate", endD.toISOString());
        }
      }

      // TODO: If error volume scales significantly, transition from client-side aggregation of raw events (currently capped at 1,000)
      // to a dedicated backend aggregation endpoint (e.g., GET /admin/errors/trends?groupBy=day,source,severity) to avoid transferring raw payloads.
      params.append("limit", "1000");

      const response = await fetch(`${API_URL}/admin/errors?${params.toString()}`, {
        signal: abortController.signal,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load chart error data: ${response.statusText}`);
      }

      const data = await response.json();
      if (requestId === activeChartRequestIdRef.current) {
        setChartItems(data.entries || []);
        setTotalCount(data.totalCount || (data.entries || []).length);
      }
    } catch (err: any) {
      if (err.name === "AbortError") return;
      if (requestId === activeChartRequestIdRef.current) {
        setErrorMsg(err.message || "Failed to load error chart metrics.");
      }
    } finally {
      if (requestId === activeChartRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [environment, source, severity, resolvedFilter, chartStartDate, chartEndDate, userInfo?.accessToken]);

  useEffect(() => {
    fetchChartErrors();
  }, [fetchChartErrors]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const { chartData, detectedSources, detectedSeverities } = useMemo(() => {
    return aggregateErrorTrends(chartItems, chartStartDate, chartEndDate, source);
  }, [chartItems, chartStartDate, chartEndDate, source]);

  const isTruncated = totalCount > chartItems.length;

  return (
    <Paper sx={{ p: 2.5, mb: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" fontWeight="bold">
              System Errors & Warnings Trends
            </Typography>
            {isTruncated && (
              <Chip
                label={`Showing latest ${chartItems.length.toLocaleString()} of ${totalCount.toLocaleString()} events`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Errors & Warnings count grouped by day and source (warnings on top, stacks labeled with source initial)
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            label="Chart Start Date"
            type="date"
            value={chartStartDate}
            onChange={(e) => setChartStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Chart End Date"
            type="date"
            value={chartEndDate}
            onChange={(e) => setChartEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Stack>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 260 }}>
          <CircularProgress size={32} />
        </Box>
      ) : chartItems.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
          <Typography variant="body2" color="text.secondary">
            No error logs found for the selected chart time window.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", height: 360 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 22, right: 30, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomChartTooltip />} />
              <Legend
                verticalAlign="bottom"
                content={() => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.25,
                      mt: 1.5,
                    }}
                  >
                    {/* Source Stack Initial Badges */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 2,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Stack Labels:
                      </Typography>
                      {detectedSources.map((src) => (
                        <Box key={src} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 18,
                              height: 18,
                              borderRadius: "4px",
                              bgcolor: "action.selected",
                              fontSize: 10,
                              fontWeight: "bold",
                              color: "text.primary",
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            {getSourceInitial(src)}
                          </Box>
                          <Typography variant="caption" sx={{ fontSize: 12 }}>
                            {SOURCE_DISPLAY_NAMES[src] || src}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Severity Colors */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 2.5,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Severities:
                      </Typography>
                      {KNOWN_SEVERITIES.map((sev) => (
                        <Box key={sev} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              bgcolor: SEVERITY_COLORS[sev],
                              borderRadius: 0.5,
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: 12 }}>
                            {SEVERITY_LABELS[sev] || sev}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              />

              {/* Group by Source, then stack by Severity order: fatal -> error -> info -> warn (warn on top) */}
              {detectedSources.map((src) => {
                const orderedSeverities = ["fatal", "error", "info", "warn"].filter((s) =>
                  detectedSeverities.includes(s)
                );

                return orderedSeverities.map((sev, idx) => {
                  const dataKey = `${src}__${sev}`;
                  const color = SEVERITY_COLORS[sev] || "#757575";

                  return (
                    <Bar
                      key={dataKey}
                      dataKey={dataKey}
                      name={src}
                      stackId={src}
                      fill={color}
                      legendType="none"
                      shape={renderBarShape(src, sev)}
                      radius={
                        idx === orderedSeverities.length - 1
                          ? [4, 4, 0, 0]
                          : [0, 0, 0, 0]
                      }
                    />
                  );
                });
              })}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
}
